import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import TranslateIcon from '@mui/icons-material/TranslateOutlined';
import { TranslateDialog } from './TranslateDialog';
import { exportEBXlsx } from '../lib/expected-behaviour-export';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { isDone } from '../lib/status';
import { fmtInt } from '../lib/format';
import {
  computeEBStats,
  expectedBehaviourText,
  qualityBucket,
  detectLang,
  QUALITY_LABELS,
  QUALITY_COLORS,
  type QualityBucket,
  type Lang,
} from '../lib/expected-behaviour';
import type { Issue } from '../types/issue';

const JIRA_URL_KEY = 'v2.jira.baseUrl';
function loadJiraBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://schueco.atlassian.net';
  return (localStorage.getItem(JIRA_URL_KEY) || 'https://schueco.atlassian.net').replace(/\/+$/, '');
}
const jiraUrl = (base: string, key: string) => `${base}/browse/${encodeURIComponent(key)}`;

const LANG_LABEL: Record<Lang, string> = {
  de:      'Deutsch',
  en:      'Englisch',
  mixed:   'gemischt DE/EN',
  unknown: 'unbekannt',
};
const LANG_COLOR: Record<Lang, string> = {
  de:      '#2e7d32',
  en:      '#1976d2',
  mixed:   '#ef6c00',
  unknown: '#9e9e9e',
};

type StatusScope = 'open' | 'closed' | 'all';

export function ExpectedBehaviour() {
  const rowsFull = useFilteredRows() as Issue[];
  const allRows = useData(s => s.rows) as Issue[];
  const teamFilter = useFilters(s => s.team);
  const setFilters = useFilters(s => s.set);
  const jiraBase = useMemo(() => loadJiraBaseUrl(), []);
  const [bucketFilter, setBucketFilter] = useState<QualityBucket | null>(null);
  const [langFilter, setLangFilter]     = useState<Lang | null>(null);
  const [translateOpen, setTranslateOpen] = useState(false);
  const [scope, setScope] = useState<StatusScope>('open');

  const allTeams = useMemo(
    () => sortedByCount(countBy(allRows, 'Team Name')),
    [allRows]
  );

  // Status-Scope-Filter — Default: nur offene (closed/resolved/aborted raus).
  // Qualitätsübersicht macht nur für aktuell offene Tickets Sinn, sonst zeigt
  // sie Altlasten die niemand mehr pflegen wird.
  const rows = useMemo(() => {
    if (scope === 'all') return rowsFull;
    return rowsFull.filter(r => {
      const done = isDone(String(r['Status'] ?? ''));
      return scope === 'open' ? !done : done;
    });
  }, [rowsFull, scope]);

  const stats = useMemo(() => computeEBStats(rows), [rows]);

  // Alle Tickets im aktuellen Filter, deren EB-Feld deutsch ist
  const germanEBRows = useMemo(() => {
    const out: { row: Issue; eb: string }[] = [];
    for (const r of rows) {
      const eb = expectedBehaviourText(r);
      if (!eb) continue;
      if (detectLang(eb) === 'de') out.push({ row: r, eb });
    }
    return out;
  }, [rows]);

  function downloadGermanCsv() {
    if (!germanEBRows.length) return;
    const teamSuffix = teamFilter ? `_${teamFilter.replace(/\W+/g, '-')}` : '';
    const stamp      = new Date().toISOString().slice(0, 10);
    const filename   = `expected-behaviour-DE${teamSuffix}_${stamp}.csv`;
    const headers = [
      'Issue key', 'Issue Type', 'Team Name', 'Summary',
      'Custom field (Expected Behaviour)',
    ];
    const lines: string[] = [headers.map(csvEscape).join(',')];
    for (const { row, eb } of germanEBRows) {
      lines.push([
        String(row['Issue key'] ?? ''),
        String(row['Issue Type'] ?? ''),
        String(row['Team Name'] ?? ''),
        String(row['Summary'] ?? ''),
        eb,
      ].map(csvEscape).join(','));
    }
    // BOM voran, damit Excel UTF-8 richtig erkennt
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
  }

  // Sample-Tickets für den aktuellen Bucket/Lang-Filter
  const samples = useMemo(() => {
    if (!bucketFilter && !langFilter) return [];
    const out: { row: Issue; eb: string; len: number; lang: Lang }[] = [];
    for (const r of rows) {
      const eb = expectedBehaviourText(r);
      const b  = qualityBucket(eb);
      if (bucketFilter && b !== bucketFilter) continue;
      const lang = eb ? detectLang(eb) : 'unknown';
      if (langFilter && lang !== langFilter) continue;
      out.push({ row: r, eb, len: eb.length, lang });
      if (out.length >= 200) break;
    }
    return out;
  }, [rows, bucketFilter, langFilter]);

  const coveragePct = stats.total > 0 ? (stats.withEB / stats.total) * 100 : 0;

  return (
    <Stack spacing={2}>
      {/* Team-Filter-Chips — wirken global (auch auf andere Sections) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
          p: 1,
          bgcolor: 'action.hover',
          borderRadius: 2,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5, color: 'text.secondary' }}>
          TEAM
        </Typography>
        <Chip
          size="small"
          label="Alle"
          color={teamFilter === '' ? 'primary' : 'default'}
          variant={teamFilter === '' ? 'filled' : 'outlined'}
          onClick={() => setFilters({ team: '' })}
          clickable
        />
        {allTeams.map(([name, count]) => (
          <Chip
            key={name}
            size="small"
            label={`${name} (${count})`}
            color={teamFilter === name ? 'primary' : 'default'}
            variant={teamFilter === name ? 'filled' : 'outlined'}
            onClick={() => setFilters({ team: name })}
            clickable
          />
        ))}
      </Box>

      {/* Status-Scope: nur offen (Default) / nur geschlossen / alle */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
          STATUS
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={scope}
          exclusive
          onChange={(_, v) => v && setScope(v as StatusScope)}
        >
          <ToggleButton value="open">nur offen</ToggleButton>
          <ToggleButton value="closed">nur geschlossen</ToggleButton>
          <ToggleButton value="all">alle</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          Geschlossen = Status Closed / Resolved / Aborted
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          disabled={rows.length === 0}
          onClick={() => exportEBXlsx(rows, {
            jiraBaseUrl: jiraBase,
            team: teamFilter,
            scope,
          })}
        >
          Excel-Export ({fmtInt(rows.length)})
        </Button>
      </Stack>

      {/* Zentrale KPI-Zeile */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <KpiCard title="Σ Tickets" value={fmtInt(stats.total)} />
        <KpiCard
          title="mit Expected Behaviour"
          value={fmtInt(stats.withEB)}
          subtitle={`${coveragePct.toFixed(1)}% Abdeckung`}
          accent="#2e7d32"
          progress={coveragePct}
        />
        <KpiCard
          title="ohne Expected Behaviour"
          value={fmtInt(stats.withoutEB)}
          subtitle={`${(100 - coveragePct).toFixed(1)}% fehlt`}
          accent="#c62828"
        />
        <KpiCard
          title="Ø Zeichen (wenn gefüllt)"
          value={fmtInt(stats.avgLen)}
          subtitle={`Median ${fmtInt(stats.medianLen)}`}
        />
      </Stack>

      {/* Abdeckung pro Typ + Team */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Abdeckung pro Issue-Type
            </Typography>
            <CoverageTable rows={stats.perType.map(x => ({ label: x.type, total: x.total, filled: x.withEB }))} />
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Abdeckung pro Team
            </Typography>
            <CoverageTable rows={stats.perTeam.map(x => ({ label: x.team, total: x.total, filled: x.withEB }))} />
          </CardContent>
        </Card>
      </Stack>

      {/* Sprach- und Qualitäts-Buckets nebeneinander */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Sprache · nur Tickets mit EB
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Heuristik über Marker-Wörter. Klick zum Filtern.
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {(['de','en','mixed','unknown'] as Lang[]).map(l => {
                const n = stats.langCounts[l];
                const pct = stats.withEB > 0 ? (n / stats.withEB) * 100 : 0;
                const active = langFilter === l;
                return (
                  <Chip
                    key={l}
                    size="small"
                    label={`${LANG_LABEL[l]}: ${fmtInt(n)} (${pct.toFixed(0)}%)`}
                    onClick={() => setLangFilter(active ? null : l)}
                    variant={active ? 'filled' : 'outlined'}
                    sx={{
                      bgcolor: active ? LANG_COLOR[l] : 'transparent',
                      color:  active ? 'white' : LANG_COLOR[l],
                      borderColor: LANG_COLOR[l],
                      fontWeight: active ? 700 : 500,
                    }}
                  />
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Qualität · alle Tickets
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Nach Textlänge des EB-Feldes. Klick zum Filtern.
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {(['empty','stub','short','ok','long','huge'] as QualityBucket[]).map(b => {
                const n = stats.qualityCounts[b];
                const pct = stats.total > 0 ? (n / stats.total) * 100 : 0;
                const active = bucketFilter === b;
                return (
                  <Chip
                    key={b}
                    size="small"
                    label={`${QUALITY_LABELS[b]}: ${fmtInt(n)} (${pct.toFixed(0)}%)`}
                    onClick={() => setBucketFilter(active ? null : b)}
                    variant={active ? 'filled' : 'outlined'}
                    sx={{
                      bgcolor: active ? QUALITY_COLORS[b] : 'transparent',
                      color:  active ? 'white' : QUALITY_COLORS[b],
                      borderColor: QUALITY_COLORS[b],
                      fontWeight: active ? 700 : 500,
                    }}
                  />
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Übersetzungs-Export für deutsche EB-Einträge */}
      {germanEBRows.length > 0 && (
        <Card variant="outlined" sx={{ borderColor: 'primary.main', borderStyle: 'dashed' }}>
          <CardContent sx={{ py: 1.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" color="primary.main" sx={{ display: 'block', lineHeight: 1.2 }}>
                  Deutsch → Englisch übersetzen
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  <b>{fmtInt(germanEBRows.length)}</b> Tickets mit deutschem Expected-Behaviour im aktuellen Filter
                  {teamFilter && <> (Team „{teamFilter}")</>}. Der deutsche Text bleibt erhalten,
                  die englische Übersetzung wird darunter angehängt.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<TranslateIcon />}
                  onClick={() => setTranslateOpen(true)}
                >
                  Übersetzen (in App)
                </Button>
                <Tooltip arrow title="Nur CSV-Export ohne Übersetzung — für den externen Skript-Workflow">
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={downloadGermanCsv}
                  >
                    CSV
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      <TranslateDialog
        open={translateOpen}
        onClose={() => setTranslateOpen(false)}
        team={teamFilter || null}
      />

      {/* Sample-Liste bei aktivem Filter */}
      {(bucketFilter || langFilter) && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
                Beispiele
                {bucketFilter && <> · Qualität: <b>{QUALITY_LABELS[bucketFilter]}</b></>}
                {langFilter   && <> · Sprache: <b>{LANG_LABEL[langFilter]}</b></>}
              </Typography>
              <Chip
                size="small"
                label="Filter zurücksetzen"
                onClick={() => { setBucketFilter(null); setLangFilter(null); }}
                variant="outlined"
              />
            </Stack>
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Typ</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Länge</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Sprache</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Summary</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Expected Behaviour</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {samples.map(({ row, eb, len, lang }) => {
                    const key = String(row['Issue key'] ?? '');
                    return (
                      <TableRow key={key} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          <Link href={jiraUrl(jiraBase, key)} target="_blank" rel="noopener noreferrer" underline="hover"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                            {key}
                            <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={String(row['Issue Type'] ?? '')}
                            variant="outlined"
                            sx={{ height: 20, fontSize: 10.5 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{String(row['Team Name'] ?? '–')}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                          {fmtInt(len)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={LANG_LABEL[lang]}
                            sx={{ height: 18, fontSize: 10, color: LANG_COLOR[lang], borderColor: LANG_COLOR[lang] }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, maxWidth: 260 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(row['Summary'] ?? '')}>
                            {String(row['Summary'] ?? '')}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, maxWidth: 480 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: eb ? 'text.primary' : 'text.disabled' }} title={eb || '(leer)'}>
                            {eb || <em>(leer)</em>}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {samples.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                        Keine Tickets in dieser Auswahl.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {samples.length === 200 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Anzeige auf 200 Zeilen begrenzt — Team-Filter oben nutzen zum Verkleinern.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

// CSV-Escape: Quoten wenn Komma, Anführungszeichen oder Zeilenumbruch drin
// ist. Anführungszeichen werden gedoppelt (RFC 4180).
function csvEscape(s: string): string {
  if (s == null) return '';
  const str = String(s);
  if (/[,"\r\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

// ─── Sub-Komponenten ─────────────────────────────────────────────────────

function KpiCard({
  title, value, subtitle, accent, progress,
}: {
  title: string; value: string; subtitle?: string; accent?: string; progress?: number;
}) {
  return (
    <Card sx={{ flex: 1, minWidth: 200 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="overline" color="text.secondary">{title}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: accent }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {subtitle}
          </Typography>
        )}
        {typeof progress === 'number' && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 1, height: 6, borderRadius: 1, bgcolor: '#61616120',
              '& .MuiLinearProgress-bar': { bgcolor: accent ?? 'primary.main' } }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function CoverageTable({ rows }: { rows: { label: string; total: number; filled: number }[] }) {
  return (
    <Table size="small">
      <TableBody>
        {rows.map(r => {
          const pct = r.total > 0 ? (r.filled / r.total) * 100 : 0;
          return (
            <TableRow key={r.label}>
              <TableCell sx={{ pl: 0, py: 0.5, borderBottom: 'none' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.label}</Typography>
              </TableCell>
              <TableCell sx={{ py: 0.5, borderBottom: 'none', width: 120 }}>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    height: 6, borderRadius: 1, bgcolor: '#61616120',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: pct >= 70 ? '#2e7d32' : pct >= 40 ? '#ef6c00' : '#c62828',
                    },
                  }}
                />
              </TableCell>
              <TableCell align="right" sx={{ py: 0.5, borderBottom: 'none', width: 90, fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                {fmtInt(r.filled)} / {fmtInt(r.total)} · {pct.toFixed(0)}%
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
